/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import apiClient, { get, post, put } from '@/services/api/client';
import type { ApiResponse } from '@/services/api/types';
import type { PagedResult } from '../types/product';
import type {
  PurchaseOrderListItem,
  PurchaseOrderDetail,
  PurchaseOrderStats,
  CreatePurchaseOrderRequest,
  UpdatePurchaseOrderRequest,
  PurchaseOrderListParams,
} from '../types/purchase-order';

const BASE = '/venta/purchase-orders';

export async function getPurchaseOrders(
  params: PurchaseOrderListParams = {}
): Promise<PagedResult<PurchaseOrderListItem>> {
  const query: Record<string, unknown> = {};
  if (params.search)      query.search      = params.search;
  if (params.status)      query.status      = params.status;
  if (params.partnerId)   query.partnerId   = params.partnerId;
  if (params.dateFrom)    query.dateFrom    = params.dateFrom;
  if (params.dateTo)      query.dateTo      = params.dateTo;
  if (params.page !== undefined) query.page = params.page;
  if (params.size !== undefined) query.size = params.size;
  if (params.sort)        query.sort        = params.sort;
  if (params.direction)   query.direction   = params.direction;

  const response = await get<ApiResponse<PagedResult<PurchaseOrderListItem>>>(BASE, query);
  return response.data;
}

export async function getPurchaseOrder(id: string): Promise<PurchaseOrderDetail> {
  const response = await get<ApiResponse<PurchaseOrderDetail>>(`${BASE}/${id}`);
  return response.data;
}

export async function getPurchaseOrderStats(): Promise<PurchaseOrderStats> {
  const response = await get<ApiResponse<PurchaseOrderStats>>(`${BASE}/stats`);
  return response.data;
}

export async function createPurchaseOrder(
  data: CreatePurchaseOrderRequest
): Promise<PurchaseOrderDetail> {
  const response = await post<ApiResponse<PurchaseOrderDetail>>(BASE, data);
  return response.data;
}

export async function updatePurchaseOrder(
  id: string,
  data: UpdatePurchaseOrderRequest
): Promise<PurchaseOrderDetail> {
  const response = await put<ApiResponse<PurchaseOrderDetail>>(`${BASE}/${id}`, data);
  return response.data;
}

export async function confirmPurchaseOrder(id: string): Promise<PurchaseOrderDetail> {
  const response = await post<ApiResponse<PurchaseOrderDetail>>(`${BASE}/${id}/confirm`, {});
  return response.data;
}

export async function cancelPurchaseOrder(id: string): Promise<PurchaseOrderDetail> {
  const response = await post<ApiResponse<PurchaseOrderDetail>>(`${BASE}/${id}/cancel`, {});
  return response.data;
}

/** Download purchase order PDF as blob */
export async function downloadPurchaseOrderPdf(
  id: string,
  template?: string,
  lang: string = 'en'
): Promise<Blob> {
  const params: Record<string, string> = { lang };
  if (template) params.template = template;
  const response = await apiClient.get(`${BASE}/${id}/pdf`, {
    params,
    responseType: 'blob',
  });
  return response.data;
}

/** Send purchase order PDF to supplier via email */
export async function sendPurchaseOrderToSupplier(
  id: string,
  lang: string = 'en'
): Promise<void> {
  await post<ApiResponse<void>>(`${BASE}/${id}/send-email`, {}, { params: { lang } });
}
